import { useCallback, useEffect, useState } from "react"
import { toast } from "sonner"
import {
  type CurrentUser,
  type VpnClient,
  type VpnClientEvent,
  type VpnConnectionSession,
  api,
  formatDate,
} from "@/lib/api"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Checkbox } from "@/components/ui/checkbox"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Skeleton } from "@/components/ui/skeleton"
import {
  ArrowLeft,
  Download,
  FileDown,
  History,
  Mail,
  Pencil,
  Plug,
  RefreshCw,
  Save,
  Settings2,
  ShieldCheck,
  ShieldOff,
  Trash2,
} from "lucide-react"
import { VpnPasswordDialog } from "@/components/VpnPasswordDialog"
import { VpnPasswordUnavailableDialog } from "@/components/VpnPasswordUnavailableDialog"
import {
  getRememberedVpnPassword,
  rememberVpnPassword,
} from "@/lib/vpn-password-cache"

type DeliveryMethod = "download" | "email"

type Props = {
  selectedMachine: string
  currentUser: CurrentUser
  clientName: string
  onBack: () => void
}

export function VpnClientDetailPage({
  selectedMachine,
  currentUser,
  clientName,
  onBack,
}: Props) {
  const [client, setClient] = useState<VpnClient | null>(null)
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)

  const [events, setEvents] = useState<VpnClientEvent[]>([])
  const [eventsLoading, setEventsLoading] = useState(true)
  const [sessions, setSessions] = useState<VpnConnectionSession[]>([])
  const [sessionsLoading, setSessionsLoading] = useState(true)

  const [fullName, setFullName] = useState("")
  const [email, setEmail] = useState("")
  const [savingProfile, setSavingProfile] = useState(false)

  const [revokeOpen, setRevokeOpen] = useState(false)
  const [revoking, setRevoking] = useState(false)

  const [deleteOpen, setDeleteOpen] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState("")
  const [deleteAcknowledged, setDeleteAcknowledged] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const [activateOpen, setActivateOpen] = useState(false)
  const [activateAutoPassword, setActivateAutoPassword] = useState(true)
  const [activatePassword, setActivatePassword] = useState("")
  const [activatePasswordConfirm, setActivatePasswordConfirm] = useState("")
  const [activateDeliveryMethod, setActivateDeliveryMethod] =
    useState<DeliveryMethod | "">("")
  const [activating, setActivating] = useState(false)

  const [downloadPassword, setDownloadPassword] = useState<{
    username: string
    password: string
  } | null>(null)
  const [unavailablePassword, setUnavailablePassword] = useState<{
    username: string
    action: "download" | "email"
  } | null>(null)

  const canDownload = currentUser.permissions.includes("download")
  const canCreate = currentUser.permissions.includes("create")
  const isAdmin = currentUser.permissions.includes("admin")

  const loadClient = useCallback(async () => {
    if (!selectedMachine || !clientName) return
    setLoading(true)
    try {
      const res = await api.getClients(selectedMachine, "all")
      const match = res.clients.find((c) => c.name === clientName) ?? null
      setClient(match)
      setNotFound(match === null)
      if (match) {
        setFullName(match.fullName || "")
        setEmail(match.email || "")
      }
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Erro ao carregar usuário."
      )
    } finally {
      setLoading(false)
    }
  }, [selectedMachine, clientName])

  const loadEvents = useCallback(async () => {
    if (!selectedMachine || !clientName) return
    setEventsLoading(true)
    try {
      const res = await api.getClientEvents(selectedMachine, clientName)
      setEvents(res.events)
    } catch {
      toast.error("Erro ao carregar auditoria.")
    } finally {
      setEventsLoading(false)
    }
  }, [selectedMachine, clientName])

  const loadSessions = useCallback(async () => {
    if (!selectedMachine || !clientName) return
    setSessionsLoading(true)
    try {
      const res = await api.getClientConnections(selectedMachine, clientName)
      setSessions(res.sessions)
    } catch {
      toast.error("Erro ao carregar histórico de conexão.")
    } finally {
      setSessionsLoading(false)
    }
  }, [selectedMachine, clientName])

  useEffect(() => {
    void loadClient()
    void loadEvents()
    void loadSessions()
  }, [loadClient, loadEvents, loadSessions])

  const refreshAll = useCallback(async () => {
    await Promise.all([loadClient(), loadEvents(), loadSessions()])
  }, [loadClient, loadEvents, loadSessions])

  const handleSaveProfile = async () => {
    if (!fullName.trim()) {
      toast.error("Informe o nome completo do usuário.")
      return
    }
    if (!email.trim()) {
      toast.error("Informe o e-mail do usuário.")
      return
    }
    setSavingProfile(true)
    try {
      const res = await api.updateVpnProfile(selectedMachine, clientName, {
        fullName: fullName.trim(),
        email: email.trim(),
      })
      toast.success(res.log || "Cadastro atualizado.")
      await Promise.all([loadClient(), loadEvents()])
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao salvar cadastro.")
    } finally {
      setSavingProfile(false)
    }
  }

  const handleDownload = async () => {
    try {
      await api.downloadVpnFile(selectedMachine, clientName)
      toast.success(`Arquivo ${clientName}.ovpn baixado.`)
      const rememberedPassword = getRememberedVpnPassword(
        selectedMachine,
        clientName
      )
      if (rememberedPassword) {
        setDownloadPassword({ username: clientName, password: rememberedPassword })
      } else {
        setUnavailablePassword({ username: clientName, action: "download" })
      }
      void loadEvents()
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Erro ao baixar arquivo."
      )
    }
  }

  const handleSendEmail = async () => {
    const rememberedPassword = getRememberedVpnPassword(
      selectedMachine,
      clientName
    )
    if (!rememberedPassword) {
      setUnavailablePassword({ username: clientName, action: "email" })
      return
    }
    try {
      const res = await api.sendVpnEmail(
        selectedMachine,
        clientName,
        rememberedPassword
      )
      toast.success(res.log || "E-mail enviado.")
      void loadEvents()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao enviar e-mail.")
    }
  }

  const handleRevoke = async () => {
    setRevoking(true)
    try {
      const res = await api.revokeVpnUser(selectedMachine, clientName)
      toast.success(res.log || "Usuário revogado.")
      await refreshAll()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao revogar.")
    } finally {
      setRevoking(false)
      setRevokeOpen(false)
    }
  }

  const closeDeleteDialog = () => {
    setDeleteOpen(false)
    setDeleteConfirm("")
    setDeleteAcknowledged(false)
  }

  const deleteConfirmMatches = deleteConfirm === clientName
  const canConfirmDelete = deleteConfirmMatches && deleteAcknowledged

  const handleDelete = async () => {
    if (!canConfirmDelete) return
    setDeleting(true)
    try {
      const res = await api.deleteVpnUser(selectedMachine, clientName)
      toast.success(res.log || "Usuário apagado do banco.")
      closeDeleteDialog()
      onBack()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao apagar usuário.")
    } finally {
      setDeleting(false)
    }
  }

  const closeActivateDialog = () => {
    setActivateOpen(false)
    setActivateAutoPassword(true)
    setActivatePassword("")
    setActivatePasswordConfirm("")
    setActivateDeliveryMethod("")
  }

  const handleActivate = async () => {
    if (!activateAutoPassword && activatePassword !== activatePasswordConfirm) {
      toast.error("A senha e a confirmação não coincidem.")
      return
    }
    if (!activateDeliveryMethod) {
      toast.error("Escolha se deseja baixar o arquivo ou enviar por e-mail.")
      return
    }
    setActivating(true)
    try {
      const manualPassword = activateAutoPassword ? "" : activatePassword
      const selectedDeliveryMethod = activateDeliveryMethod
      const res = await api.activateVpnUser({
        machineName: selectedMachine,
        clientName,
        password: activateAutoPassword ? undefined : manualPassword,
        deliveryMethod: selectedDeliveryMethod,
      })
      toast.success("Usuário reativado.")
      if (selectedDeliveryMethod === "download") {
        try {
          await api.downloadVpnFile(selectedMachine, clientName)
          toast.success(`Arquivo ${clientName}.ovpn baixado.`)
        } catch (downloadErr) {
          toast.error(
            downloadErr instanceof Error
              ? downloadErr.message
              : "Erro ao baixar arquivo."
          )
        }
        const passwordToShow = res.generatedPassword ?? manualPassword
        if (passwordToShow) {
          rememberVpnPassword(selectedMachine, clientName, passwordToShow)
          setDownloadPassword({ username: clientName, password: passwordToShow })
        }
      } else {
        if (manualPassword) {
          rememberVpnPassword(selectedMachine, clientName, manualPassword)
        }
        if (res.mailLog) toast.info(res.mailLog)
      }
      await refreshAll()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao ativar.")
    } finally {
      setActivating(false)
      closeActivateDialog()
    }
  }

  const isActive = client?.status === "active"
  const initials = (client?.fullName || clientName)
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase()

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-6 overflow-hidden p-4 sm:p-6 md:p-8">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <Button
            size="sm"
            variant="outline"
            onClick={onBack}
            className="mt-0.5"
          >
            <ArrowLeft />
            Voltar
          </Button>
          <div className="flex items-center gap-3">
            <div className="flex size-11 shrink-0 items-center justify-center rounded-lg border border-primary/25 bg-primary/15 font-mono text-sm text-primary">
              {initials || "?"}
            </div>
            <div>
              <h1 className="flex items-center gap-2 text-lg font-semibold tracking-tight">
                {client?.fullName || clientName}
                {client && (
                  <Badge
                    variant={isActive ? "default" : "secondary"}
                    className="font-mono text-[10px]"
                  >
                    {isActive ? "ativo" : "inativo"}
                  </Badge>
                )}
              </h1>
              <p className="font-mono text-xs text-muted-foreground">
                {clientName}
                {client?.email ? ` · ${client.email}` : ""}
              </p>
            </div>
          </div>
        </div>
        <Button size="sm" variant="outline" onClick={() => void refreshAll()}>
          <RefreshCw />
          Atualizar
        </Button>
      </div>

      {loading ? (
        <div className="space-y-3">
          <Skeleton className="h-8 w-72" />
          <Skeleton className="h-48 w-full" />
        </div>
      ) : notFound ? (
        <Card>
          <CardContent className="py-10 text-sm text-muted-foreground">
            Usuário não encontrado neste ambiente. Ele pode ter sido removido ou
            pertencer a outro ambiente.
          </CardContent>
        </Card>
      ) : (
        <Tabs
          defaultValue="actions"
          className="flex min-h-0 flex-1 flex-col gap-4"
        >
          <TabsList className="w-full max-w-md">
            <TabsTrigger value="actions">
              <Settings2 />
              Ações
            </TabsTrigger>
            <TabsTrigger value="audit">
              <History />
              Auditoria
            </TabsTrigger>
            <TabsTrigger value="connections">
              <Plug />
              Conexões
            </TabsTrigger>
          </TabsList>

          <TabsContent value="actions" className="mt-0">
            <div className="grid gap-4 lg:grid-cols-2">
              {canCreate && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium">
                      Cadastro
                    </CardTitle>
                    <CardDescription className="text-xs">
                      Altere ou cadastre o nome e o e-mail do usuário.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="flex flex-col gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="detail-fullname">Nome completo *</Label>
                      <Input
                        id="detail-fullname"
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        disabled={savingProfile}
                        autoComplete="off"
                        placeholder="Nome do usuário"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="detail-email">E-mail *</Label>
                      <Input
                        id="detail-email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        disabled={savingProfile}
                        autoComplete="off"
                        placeholder="email@exemplo.com"
                      />
                    </div>
                    <div>
                      <Button
                        size="sm"
                        onClick={() => void handleSaveProfile()}
                        disabled={
                          savingProfile || !fullName.trim() || !email.trim()
                        }
                      >
                        <Save />
                        {savingProfile ? "Salvando..." : "Salvar cadastro"}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium">Acesso</CardTitle>
                    <CardDescription className="text-xs">
                      Distribua o perfil ou gerencie o acesso do usuário.
                    </CardDescription>
                </CardHeader>
                <CardContent className="flex flex-col gap-2">
                  {canDownload && isActive && (
                    <>
                      <Button
                        variant="outline"
                        className="justify-start"
                        onClick={() => void handleDownload()}
                      >
                        <FileDown />
                        Baixar .ovpn
                      </Button>
                      <Button
                        variant="outline"
                        className="justify-start"
                        onClick={() => void handleSendEmail()}
                      >
                        <Mail />
                        Enviar por e-mail
                      </Button>
                    </>
                  )}
                  {canCreate &&
                    (isActive ? (
                      <Button
                        variant="outline"
                        className="justify-start text-destructive hover:text-destructive"
                        onClick={() => setRevokeOpen(true)}
                      >
                        <ShieldOff />
                        Revogar acesso
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        className="justify-start"
                        onClick={() => setActivateOpen(true)}
                      >
                        <ShieldCheck />
                        Reativar acesso
                      </Button>
                    ))}
                  {isAdmin && (
                    <Button
                      variant="outline"
                      className="justify-start text-destructive hover:text-destructive"
                      onClick={() => {
                        setDeleteConfirm("")
                        setDeleteAcknowledged(false)
                        setDeleteOpen(true)
                      }}
                    >
                      <Trash2 />
                      Apagar do banco
                    </Button>
                  )}
                  {!canDownload && !canCreate && !isAdmin && (
                    <p className="text-sm text-muted-foreground">
                      Você não tem permissão para executar ações neste usuário.
                    </p>
                  )}
                </CardContent>
              </Card>

              <Card className="lg:col-span-2">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium">
                    Informações
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-4">
                    <InfoItem label="Criação" value={formatDate(client?.createdAt ?? "")} />
                    <InfoItem
                      label="Última conexão"
                      value={formatDate(client?.lastConnectedAt ?? "")}
                    />
                    <InfoItem
                      label="Revogação"
                      value={client?.revokedAt ? formatDate(client.revokedAt) : "-"}
                    />
                    <InfoItem
                      label="Criado por"
                      value={client?.createdByAppUser || "-"}
                    />
                  </dl>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="audit" className="mt-0">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium">Auditoria</CardTitle>
                <CardDescription className="text-xs">
                  Criações, revogações, reemissões, downloads, e-mails e
                  alterações de cadastro.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="max-h-[28rem]">
                  {eventsLoading ? (
                    <div className="space-y-2 py-1">
                      {[...Array(4)].map((_, i) => (
                        <Skeleton key={i} className="h-14 w-full" />
                      ))}
                    </div>
                  ) : events.length === 0 ? (
                    <p className="py-6 text-left text-sm text-muted-foreground">
                      Nenhum registro de auditoria encontrado.
                    </p>
                  ) : (
                    <div className="flex flex-col gap-2 py-1">
                      {[...events].reverse().map((ev, i) => (
                        <div
                          key={i}
                          className="flex gap-3 rounded-lg border p-3"
                        >
                          <EventIcon type={ev.eventType} />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-xs font-medium">
                                {ev.note}
                              </span>
                              <span className="shrink-0 font-mono text-[10px] text-muted-foreground">
                                {formatDate(ev.eventAt)}
                              </span>
                            </div>
                            {ev.appUsername && (
                              <p className="mt-0.5 font-mono text-[10px] text-muted-foreground">
                                por {ev.appUsername}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="connections" className="mt-0">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium">
                  Histórico de conexão
                </CardTitle>
                <CardDescription className="text-xs">
                  Sessões de conexão detectadas nas sincronizações do ambiente.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="max-h-[28rem]">
                  {sessionsLoading ? (
                    <div className="space-y-2 py-1">
                      {[...Array(4)].map((_, i) => (
                        <Skeleton key={i} className="h-14 w-full" />
                      ))}
                    </div>
                  ) : sessions.length === 0 ? (
                    <p className="py-6 text-left text-sm text-muted-foreground">
                      Nenhuma conexão registrada ainda. O histórico começa a ser
                      preenchido conforme o usuário se conecta.
                    </p>
                  ) : (
                    <div className="flex flex-col gap-2 py-1">
                      {sessions.map((session, i) => (
                        <div
                          key={i}
                          className="flex gap-3 rounded-lg border p-3"
                        >
                          <Plug
                            className={`mt-0.5 size-4 shrink-0 ${
                              session.active
                                ? "text-emerald-400"
                                : "text-muted-foreground"
                            }`}
                          />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-xs font-medium">
                                Conectado em{" "}
                                {formatDate(
                                  session.connectedSince || session.firstSeenAt
                                )}
                              </span>
                              {session.active ? (
                                <Badge className="shrink-0 font-mono text-[10px]">
                                  ativa
                                </Badge>
                              ) : (
                                <span className="shrink-0 font-mono text-[10px] text-muted-foreground">
                                  até {formatDate(session.endedAt ?? "")}
                                </span>
                              )}
                            </div>
                            <p className="mt-0.5 font-mono text-[10px] text-muted-foreground">
                              {session.vpnIp ? `IP VPN ${session.vpnIp}` : ""}
                              {session.vpnIp && session.realAddress ? " · " : ""}
                              {session.realAddress
                                ? `origem ${session.realAddress}`
                                : ""}
                              {!session.vpnIp && !session.realAddress
                                ? "Sem detalhes de endereço."
                                : ""}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}

      <AlertDialog
        open={revokeOpen}
        onOpenChange={(o) => !o && setRevokeOpen(false)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revogar acesso VPN</AlertDialogTitle>
            <AlertDialogDescription>
              O certificado de{" "}
              <span className="font-mono font-medium">{clientName}</span> será
              revogado e o usuário perderá acesso à VPN imediatamente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={revoking}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRevoke}
              disabled={revoking}
              className="text-destructive-foreground bg-destructive hover:bg-destructive/90"
            >
              {revoking ? "Revogando..." : "Revogar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={deleteOpen}
        onOpenChange={(o) => !o && closeDeleteDialog()}
      >
        <AlertDialogContent className="sm:max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle>Apagar usuário VPN do banco</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4 text-left text-sm text-muted-foreground">
                <p>
                  Ação crítica e irreversível. Revise o que será excluído antes
                  de confirmar.
                </p>

                <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-foreground">
                  <p className="mb-2 text-xs font-medium tracking-wide text-destructive uppercase">
                    Será excluído
                  </p>
                  <dl className="grid gap-2 text-sm">
                    <div className="flex justify-between gap-3">
                      <dt className="text-muted-foreground">Usuário VPN</dt>
                      <dd className="font-mono text-right">{clientName}</dd>
                    </div>
                    <div className="flex justify-between gap-3">
                      <dt className="text-muted-foreground">Nome</dt>
                      <dd className="text-right">
                        {client?.fullName || fullName || "-"}
                      </dd>
                    </div>
                    <div className="flex justify-between gap-3">
                      <dt className="text-muted-foreground">E-mail</dt>
                      <dd className="text-right break-all">
                        {client?.email || email || "-"}
                      </dd>
                    </div>
                    <div className="flex justify-between gap-3">
                      <dt className="text-muted-foreground">Ambiente</dt>
                      <dd className="font-mono text-right">{selectedMachine}</dd>
                    </div>
                    <div className="flex justify-between gap-3">
                      <dt className="text-muted-foreground">Status atual</dt>
                      <dd className="text-right">
                        {isActive ? "ativo" : "inativo"}
                      </dd>
                    </div>
                  </dl>
                  <ul className="mt-3 space-y-1.5 border-t border-destructive/20 pt-3 text-xs text-muted-foreground">
                    <li>
                      Cadastro do usuário neste ambiente (nome, e-mail e status)
                    </li>
                    <li>
                      {eventsLoading
                        ? "Eventos de auditoria deste usuário"
                        : `${events.length} evento(s) de auditoria`}
                    </li>
                    <li>
                      {sessionsLoading
                        ? "Histórico de conexões deste usuário"
                        : `${sessions.length} registro(s) de conexão`}
                    </li>
                    {isActive && (
                      <li className="font-medium text-destructive">
                        Certificado no servidor VPN (revogação antes da exclusão)
                      </li>
                    )}
                  </ul>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="delete-vpn-confirm" className="text-foreground">
                    Digite{" "}
                    <span className="font-mono">{clientName}</span> para
                    confirmar
                  </Label>
                  <Input
                    id="delete-vpn-confirm"
                    autoComplete="off"
                    autoFocus
                    value={deleteConfirm}
                    onChange={(e) => setDeleteConfirm(e.target.value)}
                    placeholder={clientName}
                    disabled={deleting}
                    className="font-mono"
                  />
                </div>

                <label
                  htmlFor="delete-vpn-ack"
                  className="flex cursor-pointer items-start gap-2.5 rounded-lg border p-3 text-foreground"
                >
                  <Checkbox
                    id="delete-vpn-ack"
                    checked={deleteAcknowledged}
                    onCheckedChange={(checked) =>
                      setDeleteAcknowledged(checked === true)
                    }
                    disabled={deleting}
                    className="mt-0.5"
                  />
                  <span className="text-sm leading-snug">
                    Confirmo que entendi: estes dados serão apagados
                    permanentemente e não poderão ser recuperados pelo painel.
                  </span>
                </label>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault()
                void handleDelete()
              }}
              disabled={deleting || !canConfirmDelete}
              className="text-destructive-foreground bg-destructive hover:bg-destructive/90"
            >
              {deleting ? "Apagando..." : "Confirmar exclusão"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog
        open={activateOpen}
        onOpenChange={(o) => !o && closeActivateDialog()}
      >
        <DialogContent>
          <form
            onSubmit={(e) => {
              e.preventDefault()
              void handleActivate()
            }}
            className="flex flex-col gap-4"
          >
            <DialogHeader>
              <DialogTitle>Reativar acesso VPN</DialogTitle>
              <DialogDescription>
                Um novo certificado será gerado para{" "}
                <span className="font-mono font-medium">{clientName}</span>.
              </DialogDescription>
            </DialogHeader>

            <div>
              <p className="mb-3 text-sm font-medium">Segurança</p>
              <p className="mb-3 text-xs text-muted-foreground">
                Mín. 8 caracteres: maiúscula, minúscula, número e símbolo (! @ #
                $ % * - _).
              </p>
              <div className="mb-3 grid grid-cols-2 gap-2 rounded-lg border bg-muted/20 p-1">
                <Button
                  type="button"
                  variant={activateAutoPassword ? "default" : "ghost"}
                  className="h-auto min-h-10 whitespace-normal px-3 py-2 text-xs"
                  onClick={() => setActivateAutoPassword(true)}
                  disabled={activating}
                >
                  Gerar automaticamente
                </Button>
                <Button
                  type="button"
                  variant={!activateAutoPassword ? "default" : "ghost"}
                  className="h-auto min-h-10 whitespace-normal px-3 py-2 text-xs"
                  onClick={() => setActivateAutoPassword(false)}
                  disabled={activating}
                >
                  Definir manualmente
                </Button>
              </div>
              {!activateAutoPassword && (
                <div className="flex flex-col gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="detail-activate-pw">Senha *</Label>
                    <Input
                      id="detail-activate-pw"
                      type="password"
                      value={activatePassword}
                      onChange={(e) => setActivatePassword(e.target.value)}
                      disabled={activating}
                      autoComplete="new-password"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="detail-activate-pw-confirm">
                      Confirmação *
                    </Label>
                    <Input
                      id="detail-activate-pw-confirm"
                      type="password"
                      value={activatePasswordConfirm}
                      onChange={(e) =>
                        setActivatePasswordConfirm(e.target.value)
                      }
                      disabled={activating}
                      autoComplete="new-password"
                    />
                  </div>
                </div>
              )}
            </div>

            <div>
              <p className="mb-3 text-sm font-medium">Entrega do acesso</p>
              <div className="grid grid-cols-2 gap-2 rounded-lg border bg-muted/20 p-1">
                <Button
                  type="button"
                  variant={
                    activateDeliveryMethod === "download" ? "default" : "ghost"
                  }
                  className="h-auto min-h-10 whitespace-normal px-3 py-2 text-xs"
                  onClick={() => setActivateDeliveryMethod("download")}
                  disabled={activating}
                >
                  <Download className="size-4" />
                  Download
                </Button>
                <Button
                  type="button"
                  variant={
                    activateDeliveryMethod === "email" ? "default" : "ghost"
                  }
                  className="h-auto min-h-10 whitespace-normal px-3 py-2 text-xs"
                  onClick={() => setActivateDeliveryMethod("email")}
                  disabled={activating}
                >
                  <Mail className="size-4" />
                  Enviar por e-mail
                </Button>
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={closeActivateDialog}
                disabled={activating}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={
                  activating ||
                  !activateDeliveryMethod ||
                  (!activateAutoPassword && !activatePassword)
                }
              >
                {activating ? "Reativando..." : "Reativar"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <VpnPasswordDialog
        open={downloadPassword !== null}
        username={downloadPassword?.username ?? ""}
        password={downloadPassword?.password ?? ""}
        onClose={() => setDownloadPassword(null)}
      />
      <VpnPasswordUnavailableDialog
        open={unavailablePassword !== null}
        username={unavailablePassword?.username ?? ""}
        action={unavailablePassword?.action ?? "download"}
        onClose={() => setUnavailablePassword(null)}
      />
    </div>
  )
}

function InfoItem({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="font-mono text-[10px] tracking-wide text-muted-foreground uppercase">
        {label}
      </dt>
      <dd className="mt-0.5 text-sm">{value}</dd>
    </div>
  )
}

function EventIcon({ type }: { type: VpnClientEvent["eventType"] }) {
  const map: Record<VpnClientEvent["eventType"], React.ReactNode> = {
    created: (
      <ShieldCheck className="mt-0.5 size-4 shrink-0 text-emerald-400" />
    ),
    revoked: <ShieldOff className="mt-0.5 size-4 shrink-0 text-destructive" />,
    reissued: <ShieldCheck className="mt-0.5 size-4 shrink-0 text-sky-400" />,
    downloaded: (
      <FileDown className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
    ),
    emailed: <Mail className="mt-0.5 size-4 shrink-0 text-muted-foreground" />,
    updated: <Pencil className="mt-0.5 size-4 shrink-0 text-amber-400" />,
  }
  return <>{map[type]}</>
}
